import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react'
import type React from 'react'
import { toast } from 'sonner'
import { quietToastOptions } from '@/lib/toast-chrome'
import type { Message, DocumentReference, ProposedEditData, ChangedLinesPayload } from './chat-pane.types'
import { getConversation, archiveConversation, fetchWithAuth } from '@/lib/api'
import { getThread } from '@/lib/api/threads'
import { hydrateConversationDraftScope } from '@/lib/chat-conversation-scope'
import {
  peekThreadWorkspaceMeta,
  peekThreadWorkspaceMetaByConversationId,
  rememberThreadWorkspaceMeta,
} from '@/lib/thread-workspace-meta'
import { exportConversation } from '@/lib/api/conversations'
import { openSidebarMode } from '@/lib/sidebar-mode'
import { openSettings } from '@/lib/open-settings'
import { flipIngestionConversationToStreaming, isIngestionThreadComplete } from '@/lib/ingestion-streaming-flip'
import { mapApiMessagesToChatMessages, completedFilesFromMessages } from '@/lib/chat-message-hydration'
import {
  clearPersistedBasecampCreateDraft,
  restoreBasecampCreateDraft,
  type BasecampCreateTodoDraft,
} from '@/lib/basecamp-create-todo-draft'
import type { CompletedFileRow } from './chat-pane.streaming'
import { isKnownGoneConversationId, markConversationIdGone, isPermanentlyGoneStatus } from '@/lib/conversation-load-guard'
import { prependMissingUserTurns } from './chat-pane.utils'
import { proposedEditMatches } from '@/lib/proposed-edit-utils'
import {
  enqueueUpdateWorkSpecKickoff,
  shouldKickoffSeededUpdateWorkSpec,
} from '@/lib/update-work-spec-kickoff'

export type EditHandlerOptions = {
  /** Keep the diff modal open (batch review of multiple edits). */
  keepDiffOpen?: boolean
  /** Skip the success toast (caller handles batch UX). */
  skipSuccessToast?: boolean
  /** Saved markdown. Defaults to the full proposal. */
  newContent?: string
  /** Write this change and leave the proposal pending for the edits still waiting. */
  keepPending?: boolean
  nextOriginal?: string
  nextProposed?: string
}

type ChatPaneActiveConversation = {
  id: string;
  title: string;
  message_count?: number;
  updated_at?: string;
  created_at?: string;
  metadata?: Record<string, unknown>;
};

/**
 * Utility function to normalize changed lines payload
 */
export function normalizeChangedLines(raw: any): ChangedLinesPayload | null {
  if (!raw) return null
  let value = raw
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return null
    }
  }
  if (!value || typeof value !== 'object') {
    return null
  }

  const ensureNumberArray = (arr: any): number[] => {
    if (!Array.isArray(arr)) return []
    return arr
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0)
  }

  return {
    added: ensureNumberArray(value.added),
    removed: ensureNumberArray(value.removed),
    modified: ensureNumberArray(value.modified),
    changed_at: value.changed_at,
    changed_by: value.changed_by,
    change_status: value.change_status,
    requires_acceptance: value.requires_acceptance,
    accepted_at: value.accepted_at,
    conversation_id: value.conversation_id,
  }
}

/**
 * Hook for chat-related handlers (send, stop, copy, etc.)
 */
export function useChatHandlers(
  streamingMessages: Message[],
  setStreamingMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void,
  streamingInput: string,
  setStreamingInput: (input: string) => void,
  setIsStreaming: (streaming: boolean) => void,
  setIsTyping: (typing: boolean) => void,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
  streamingConversationId: string | null,
  setStreamingConversationId: (id: string | null) => void,
  references: DocumentReference[],
  activeDocumentIdRef: React.RefObject<string | undefined>,
  selectedProvider: 'openai' | 'anthropic' | 'groq' | 'xai' | 'localai' | 'ollama',
  selectedModel: string,
  /** Active document IDs dismissed from AI Context for this conversation (suppress activeDocumentId in API when present). */
  dismissedDocIdsRef: React.RefObject<Set<string>> | undefined,
  activeConversations: ChatPaneActiveConversation[],
  setActiveConversations: Dispatch<SetStateAction<ChatPaneActiveConversation[]>>,
  setHistoryRefreshKey: (key: number | ((prev: number) => number)) => void,
  messagesContainerRef: React.RefObject<HTMLDivElement | null>,
  onClearReferences?: () => void,
  inputRef?: React.RefObject<{ getImages: () => import('./chat-pane.types').ChatImage[]; clearImages: () => void } | null>,
  activeSkillIdRef?: React.MutableRefObject<string | null>,
  activeSkillRef?: React.MutableRefObject<{ id: string; name: string } | null>,
): {
  handleStop: () => void
  handleSuggestedPrompt: (prompt: string) => void
  handleQuickAction: (action: string) => void
  handleCopyCode: (code: string) => Promise<void>
  saveMessageToHistory: (
    role: 'user' | 'assistant',
    content: string,
    conversationIdOverride?: string | null,
    options?: {
      skillId?: string
      skillName?: string
      workspaceScope?: string[] | null
      /** Live chat-context content roots for tag union (even when workspaceScope persist is NULL). */
      workspaceScopeForTags?: string[] | null
      metadata?: Record<string, unknown>
    }
  ) => Promise<{ conversationId: string | null; messageId: string | null }>
} {
  // Helper to save messages to conversation history
  const saveMessageToHistory = useCallback(async (
    role: 'user' | 'assistant',
    content: string,
    conversationIdOverride?: string | null,
    options?: {
      skillId?: string
      skillName?: string
      workspaceScope?: string[] | null
      workspaceScopeForTags?: string[] | null
      metadata?: Record<string, unknown>
    }
  ): Promise<{ conversationId: string | null; messageId: string | null }> => {
    try {
      const convId = conversationIdOverride !== undefined ? conversationIdOverride : streamingConversationId
      // Ownership is derived server-side from the authenticated request
      // (req.flowNotesUser.id via Bearer JWT) - do not send a userId here.
      const body: Record<string, unknown> = {
        conversationId: convId,
        role,
        message: content,
        ...(options?.skillId && { skillId: options.skillId, skillName: options.skillName }),
      }
      if (options?.metadata && Object.keys(options.metadata).length > 0) {
        body.metadata = options.metadata
      }
      if (options && Object.prototype.hasOwnProperty.call(options, 'workspaceScope')) {
        body.workspaceScope = options.workspaceScope
      }
      if (
        options &&
        Object.prototype.hasOwnProperty.call(options, 'workspaceScopeForTags') &&
        Array.isArray(options.workspaceScopeForTags) &&
        options.workspaceScopeForTags.length > 0
      ) {
        body.workspaceScopeForTags = options.workspaceScopeForTags
      }
      const response = await fetchWithAuth(`/api/chat-stream/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      const result = await response.json()

      if (result.success && !convId) {
        setStreamingConversationId(result.conversationId)
        console.log('[ChatPane] Created conversation:', result.conversationId)
        return { conversationId: result.conversationId, messageId: result.messageId ?? null }
      }
      return { conversationId: result.success ? (convId ?? null) : null, messageId: result.messageId ?? null }
    } catch (error) {
      console.error('[ChatPane] Failed to save message to history:', error)
      return { conversationId: null, messageId: null }
    }
  }, [streamingConversationId, setStreamingConversationId])

  const handleCopyCode = useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      // Note: copiedCode state should be managed by the component
    } catch (err) {
      console.error('Failed to copy code:', err)
    }
  }, [])

  const handleSuggestedPrompt = useCallback((prompt: string) => {
    setStreamingInput(prompt)  // Use streaming input for general chat
  }, [setStreamingInput])

  const handleQuickAction = useCallback((action: string) => {
    setStreamingInput(`Quick action: ${action}`)
  }, [setStreamingInput])

  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsStreaming(false)
    setIsTyping(false)
    // Optimistically mark the last assistant message as stopped
    setStreamingMessages(prev => {
      const msgs = [...prev]
      const last = msgs[msgs.length - 1]
      if (last?.role === 'assistant') {
        msgs[msgs.length - 1] = { ...last, aborted: true }
      }
      return msgs
    })
  }, [abortControllerRef, setIsStreaming, setIsTyping, setStreamingMessages])

  return {
    handleStop,
    handleSuggestedPrompt,
    handleQuickAction,
    handleCopyCode,
    saveMessageToHistory,
  }
}

/**
 * Hook for edit-related handlers (accept, reject, etc.)
 */
export function useEditHandlers(
  proposedEdit: ProposedEditData | null,
  setProposedEdit: Dispatch<SetStateAction<ProposedEditData | null>>,
  setStreamingMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void,
  setIsApplyingEdit: (applying: boolean) => void,
  setDiffPreviewOpen: (open: boolean) => void,
  streamingConversationId: string | null,
  activeDocumentIdRef: React.RefObject<string | undefined>,
  /** Clear agentic completed-files panel entries so proposed-edit accept does not duplicate the collapsible bar. */
  setCompletedFiles?: React.Dispatch<React.SetStateAction<Array<{
    filePath: string
    status: 'new' | 'modified'
    linesAdded: number
    linesRemoved: number
    documentId?: string
    timestamp?: string
  }>>>
) {
  // Resolve document ID from path or UUID
  const resolveDocumentId = useCallback(async (documentIdOrPath: string | undefined | null): Promise<string | null> => {
    if (!documentIdOrPath) return null

    // If it's already a UUID (36 chars with hyphens), return as-is
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(documentIdOrPath)) {
      return documentIdOrPath
    }

    // Otherwise, try to resolve from path
    try {
      const { searchDocuments } = await import('@/lib/api')
      const results = await searchDocuments({ query: `path:${documentIdOrPath}`, limit: 1 })
      if (results.results.length > 0) {
        return results.results[0].id
      }
    } catch (error) {
      console.error('[ChatPane] Failed to resolve document ID:', error)
    }

    return null
  }, [])

  const handleAcceptEdit = useCallback(async (edit?: ProposedEditData, options?: EditHandlerOptions): Promise<boolean> => {
    const targetEdit = edit ?? proposedEdit
    if (!targetEdit) {
      console.warn('[ChatPane] handleAcceptEdit called but no targetEdit available')
      return false
    }

    if (!targetEdit.documentId) {
      const currentDocId = activeDocumentIdRef.current
      console.error('[ChatPane] handleAcceptEdit called but documentId is missing', {
        targetEdit,
        proposedEdit,
        activeDocumentId: currentDocId
      })

      // Try to use activeDocumentId as fallback (read at call time)
      const fallbackDocumentId = currentDocId || targetEdit.filename
      if (fallbackDocumentId) {
        console.log('[ChatPane] Using fallback documentId:', fallbackDocumentId)
        targetEdit.documentId = fallbackDocumentId
      } else {
        toast.error('Cannot apply changes', {
          description: 'Document ID is missing'
        })
        return false
      }
    }

    setIsApplyingEdit(true)

    try {
      // Resolve file path to UUID if needed
      const resolvedDocumentId = await resolveDocumentId(targetEdit.documentId)

      if (!resolvedDocumentId) {
        throw new Error('Failed to resolve document ID')
      }

      const keepPending = Boolean(options?.keepPending)
      const nextOriginal = options?.nextOriginal ?? targetEdit.original
      const nextProposed = options?.nextProposed ?? options?.newContent ?? targetEdit.proposed

      const response = await fetchWithAuth(`/api/chat-stream/apply-edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          documentId: resolvedDocumentId,
          originalContent: targetEdit.original,
          newContent: options?.newContent ?? targetEdit.proposed,
          frontmatter: targetEdit.frontmatter,
          userId: 'default',
          conversationId: streamingConversationId,
          metadata: { source: 'chat-pane' }
        }),
      })

      const result = await response.json()
      const changedLinesPayload = normalizeChangedLines(result.changedLines ?? result.document?.changed_lines)

      if (result.success) {
        if (!options?.keepDiffOpen && !keepPending) {
          setDiffPreviewOpen(false)
        }
        const editedDocId = targetEdit.documentId
        const editedDocTitle = targetEdit.title

        // Update message status to accepted (match by sessionEditId or same documentId so
        // Files bar pending rows clear even when session ids diverge across turns).
        setStreamingMessages(prev => prev.map(m => {
          const pe = m.proposedEdit
          if (!pe) return m
          const sameDoc =
            Boolean(pe.documentId) &&
            (pe.documentId === targetEdit.documentId || pe.documentId === editedDocId)
          if (!proposedEditMatches(pe, targetEdit) && !sameDoc) return m
          return {
            ...m,
            proposedEdit: {
              ...pe,
              status: keepPending ? 'pending' as const : 'accepted' as const,
              original: keepPending ? nextOriginal : pe.original,
              proposed: keepPending ? nextProposed : pe.proposed,
              changeStatus: keepPending ? 'pending' : (changedLinesPayload?.change_status ?? 'accepted'),
              changedLines: changedLinesPayload ?? pe.changedLines ?? null,
            },
          }
        }))
        setProposedEdit((prev: ProposedEditData | null) => {
          if (!prev) return prev
          const sameDoc =
            Boolean(prev.documentId) &&
            (prev.documentId === targetEdit.documentId || prev.documentId === editedDocId)
          if (!proposedEditMatches(prev, targetEdit) && !sameDoc) return prev
          return {
            ...prev,
            status: keepPending ? 'pending' : 'accepted',
            original: keepPending ? nextOriginal : prev.original,
            proposed: keepPending ? nextProposed : prev.proposed,
            changeStatus: keepPending ? 'pending' : (changedLinesPayload?.change_status ?? 'accepted'),
            changedLines: changedLinesPayload ?? prev.changedLines ?? null,
          }
        })
        if (!edit && !options?.keepDiffOpen && !keepPending) {
          setProposedEdit(null)
        }

        // Keep the Files changed bar populated after accept (Cursor-style), so the
        // user can still open/undo from the strip above the composer.
        const linesAdded =
          targetEdit.additions ??
          changedLinesPayload?.added?.length ??
          0
        const linesRemoved =
          targetEdit.deletions ??
          changedLinesPayload?.removed?.length ??
          0
        if (!keepPending) setCompletedFiles?.((prev) => {
          const row = {
            filePath: targetEdit.filename || 'document.md',
            displayPath: targetEdit.displayPath ?? null,
            url: targetEdit.url ?? null,
            status: 'modified' as const,
            linesAdded,
            linesRemoved,
            documentId: resolvedDocumentId,
            title: editedDocTitle,
          }
          const idx = prev.findIndex(
            (f) =>
              f.documentId === resolvedDocumentId ||
              f.documentId === editedDocId ||
              f.filePath === row.filePath,
          )
          if (idx >= 0) {
            const next = [...prev]
            next[idx] = { ...next[idx], ...row }
            return next
          }
          return [...prev, row]
        })

        // Show success toast with undo option (skip during batch review)
        if (!options?.skipSuccessToast) {
        toast.success(editedDocTitle ? `Applied to ${editedDocTitle}` : 'Applied', quietToastOptions('success', {
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                // Call undo endpoint
                const undoResponse = await fetchWithAuth(`/api/chat-stream/undo-edit`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ documentId: editedDocId })
                })

                const undoResult = await undoResponse.json()

                if (undoResult.success) {
                  toast.success(editedDocTitle ? `Undone ${editedDocTitle}` : 'Undone', quietToastOptions('success'))

                  // Reload document
                  window.dispatchEvent(new CustomEvent('document-updated', {
                    detail: { documentId: editedDocId }
                  }))
                } else {
                  toast.error('Failed to undo', { description: undoResult.error })
                }
              } catch (error) {
                toast.error('Undo failed', {
                  description: error instanceof Error ? error.message : 'Unknown error'
                })
              }
            }
          }
        }))
        }

        // Reload the document (dispatch resolved UUID + file path for tab matching)
        console.log('[ChatPane] Dispatching document-updated event for:', {
          documentId: resolvedDocumentId,
          filename: targetEdit.filename,
          activeDocumentId: activeDocumentIdRef.current
        })

        window.dispatchEvent(new CustomEvent('document-updated', {
          detail: {
            documentId: resolvedDocumentId,
            filePath: targetEdit.filename,
            changedLines: changedLinesPayload ?? null
          }
        }))
        return true

      } else {
        throw new Error(
          result.details
            ? `${result.error || 'Failed to apply changes'}: ${result.details}`
            : (result.error || 'Failed to apply changes')
        )
      }
    } catch (error) {
      console.error('[ChatPane] Failed to apply edit:', error)
      toast.error('Failed to apply changes', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
      return false
    } finally {
      setIsApplyingEdit(false)
    }
  }, [
    proposedEdit,
    setProposedEdit,
    setStreamingMessages,
    setIsApplyingEdit,
    setDiffPreviewOpen,
    streamingConversationId,
    activeDocumentIdRef,
    resolveDocumentId,
    setCompletedFiles,
  ])

  const handleRejectEdit = useCallback(async (edit?: ProposedEditData, options?: EditHandlerOptions): Promise<boolean> => {
    const targetEdit = edit ?? proposedEdit
    if (!targetEdit) {
      console.warn('[ChatPane] handleRejectEdit called but no targetEdit available')
      return false
    }

    if (!targetEdit.documentId) {
      const currentDocId = activeDocumentIdRef.current
      console.error('[ChatPane] handleRejectEdit called but documentId is missing', {
        targetEdit,
        proposedEdit,
        activeDocumentId: currentDocId
      })

      // Try to use activeDocumentId as fallback (read at call time)
      const fallbackDocumentId = currentDocId || targetEdit.filename
      if (fallbackDocumentId) {
        console.log('[ChatPane] Using fallback documentId:', fallbackDocumentId)
        targetEdit.documentId = fallbackDocumentId
      } else {
        toast.error('Cannot reject changes', {
          description: 'Document ID is missing'
        })
        return false
      }
    }

    setIsApplyingEdit(true)

    try {
      // Resolve file path to UUID if needed
      const resolvedDocumentId = await resolveDocumentId(targetEdit.documentId)

      if (!resolvedDocumentId) {
        throw new Error('Failed to resolve document ID')
      }

      const response = await fetchWithAuth(`/api/chat-stream/reject-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: resolvedDocumentId,
          originalContent: targetEdit.original,
          proposedContent: targetEdit.proposed,
          conversationId: streamingConversationId,
          changedLines: targetEdit.changedLines,
          userId: 'default',
        }),
      })

      const result = await response.json()

      if (result.success) {
        if (!options?.keepDiffOpen) {
          setDiffPreviewOpen(false)
        }
        setStreamingMessages(prev => prev.map(m =>
          proposedEditMatches(m.proposedEdit, targetEdit)
            ? {
                ...m,
                proposedEdit: {
                  ...m.proposedEdit!,
                  status: 'rejected' as const,
                  changeStatus: 'rejected',
                },
              }
            : m
        ))
        setProposedEdit((prev: ProposedEditData | null) => {
          if (!prev) return prev
          if (!proposedEditMatches(prev, targetEdit)) return prev
          return { ...prev, status: 'rejected', changeStatus: 'rejected' }
        })
        if (!edit && !options?.keepDiffOpen) {
          setProposedEdit(null)
        }

        // Rejected proposals must not linger in the Files bar as "already written".
        setCompletedFiles?.((prev) =>
          prev.filter(
            (f) =>
              f.documentId !== resolvedDocumentId &&
              f.documentId !== targetEdit.documentId &&
              f.filePath !== (targetEdit.filename || ''),
          ),
        )

        if (!options?.skipSuccessToast) {
        toast.info('Edit rejected', {
          description: targetEdit.title,
        })
        }
        return true
      } else {
        throw new Error(result.error || 'Failed to reject edit')
      }
    } catch (error) {
      console.error('[ChatPane] Failed to reject edit:', error)
      toast.error('Failed to reject changes', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
      return false
    } finally {
      setIsApplyingEdit(false)
    }
  }, [
    proposedEdit,
    setProposedEdit,
    setStreamingMessages,
    setIsApplyingEdit,
    setDiffPreviewOpen,
    streamingConversationId,
    activeDocumentIdRef,
    resolveDocumentId,
    setCompletedFiles,
  ])

  const handleEditBeforeApply = useCallback(() => {
    console.log('[ChatPane] Edit before apply - not yet implemented')
    // TODO: Phase 2 - Open editor with proposed changes
    // For now, just show a message
    toast.info('Edit before apply', {
      description: 'This feature will be available in a future update'
    })
  }, [])

  return {
    handleAcceptEdit,
    handleRejectEdit,
    handleEditBeforeApply,
    resolveDocumentId,
  }
}

/**
 * Hook for conversation management handlers
 */
export function useConversationHandlers(
  setCurrentConversationId: (id: string | null) => void,
  setStreamingConversationId: (id: string | null) => void,
  setStreamingMessages: (
    messages: Message[] | ((prev: Message[]) => Message[]),
  ) => void,
  setLegacyMessages: (messages: Message[]) => void,
  setStreamingInput: (input: string) => void,
  setLegacyInput: (input: string) => void,
  setShowHistory: (show: boolean) => void,
  setActiveConversations: Dispatch<SetStateAction<ChatPaneActiveConversation[]>>,
  setHistoryRefreshKey: (key: number | ((prev: number) => number)) => void,
  currentConversationId: string | null,
  pollingIntervalRef: React.MutableRefObject<number | null>,
  setIsPolling: (polling: boolean) => void,
  deletingConversationsRef: React.MutableRefObject<Set<string>>,
  startPolling: (id: string, fastMode?: boolean) => void,
  stopPolling: () => void,
  setConversationScope?: (scope: string[] | null) => void,
  lastLoadedScopePathsRef?: React.MutableRefObject<string[] | null>,
  setThinkingModeActive?: (active: boolean) => void,
  setCompletedFiles?: React.Dispatch<React.SetStateAction<CompletedFileRow[]>>,
  streamingConversationId?: string | null,
  /** When the pane hosts an inbox/agent thread, prefer this over stale currentConversationId. */
  hostedLinkedConversationId?: string | null,
  /** Abort in-flight chat-stream when switching conversations / New chat. */
  abortControllerRef?: React.MutableRefObject<AbortController | null>,
  setIsStreaming?: (streaming: boolean) => void,
  setIsTyping?: (typing: boolean) => void,
  /** Invalidates detached stream UI writes (must bump with abort). */
  streamEpochRef?: React.MutableRefObject<number>,
  /** Clear Basecamp notes draft when leaving the conversation that produced it. */
  setPendingBasecampDraft?: (draft: null) => void,
  /** Clear or restore Basecamp create-todo confirm card when switching conversations. */
  setPendingBasecampCreateDraft?: (draft: BasecampCreateTodoDraft | null) => void,
  /** Cleared with the abort controller so a persist-time new-* promote cannot keep the old body on another thread. */
  streamInFlightRef?: React.MutableRefObject<boolean>,
  hostedThreadId?: string | null,
  userExplicitlySetScopeRef?: React.MutableRefObject<boolean>,
  workspacesRef?: React.MutableRefObject<Array<{ id: string; contentRoot?: string | null }>>,
  /** Sent prompt held across a new-* promote. Do not detach or replace it on first-send hydrate. */
  optimisticUserTurnRef?: React.MutableRefObject<Message | null>,
) {
  // Out-of-order response guard: each call to handleSelectConversation (or a reset like
  // handleNewChat) bumps this counter. If a conversation's async fetch resolves after a
  // *newer* selection/reset has already happened, its stale `.then()` continuation must not
  // overwrite the messages/state for whatever the user is now looking at. Without this, rapidly
  // switching chats in the history list (or firing a new-chat action mid-load) lets an older,
  // slower request land last and clobber the correct, newer conversation's messages while the
  // title (driven separately by the registry/thread id) already shows the new selection.
  const conversationLoadSeqRef = useRef(0)

  /** Stop SSE + invalidate UI writers so an in-flight hop/triage stream cannot paint into another chat. */
  const detachActiveStream = useCallback(() => {
    if (streamEpochRef) streamEpochRef.current += 1
    if (streamInFlightRef) streamInFlightRef.current = false
    if (abortControllerRef?.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsStreaming?.(false)
    setIsTyping?.(false)
  }, [abortControllerRef, setIsStreaming, setIsTyping, streamEpochRef, streamInFlightRef])

  const handleNewChat = useCallback(async () => {
    // FIX 2: Block new chat while an unanswered AskQuestion is live (incident 2026-07-22).
    let hasUnansweredAsk = false
    setStreamingMessages((prev) => {
      hasUnansweredAsk = prev.some((m) => m.askQuestion && !m.askQuestion.answered)
      return prev // No-op update - just reading state
    })
    
    if (hasUnansweredAsk) {
      const { toast } = await import('sonner')
      toast.error('Please answer the question first', {
        description: 'You must respond to the current question before starting a new chat.',
      })
      return
    }

    console.log("Create new conversation")
    conversationLoadSeqRef.current += 1  // Invalidate any in-flight handleSelectConversation load
    detachActiveStream()
    setCurrentConversationId(null)  // Switch to general chat mode
    setStreamingConversationId(null)  // Reset streaming conversation
    setStreamingMessages([])  // Clear streaming messages
    setLegacyMessages([])  // Clear legacy messages
    setStreamingInput("")  // Clear input
    setLegacyInput("")  // Clear legacy input
    setConversationScope?.(null)  // Reset draft scope (follows sidebar until send)
    setPendingBasecampDraft?.(null)  // Do not leak prior-chat Basecamp notes draft into New chat
    setPendingBasecampCreateDraft?.(null)
    clearPersistedBasecampCreateDraft(streamingConversationId)
    clearPersistedBasecampCreateDraft(hostedLinkedConversationId)
    if (lastLoadedScopePathsRef) lastLoadedScopePathsRef.current = null
    stopPolling()  // Stop any active polling
    sessionStorage.removeItem('flownotes_current_conversation_id')  // Clear saved conversation
  }, [
    detachActiveStream,
    setCurrentConversationId,
    setStreamingConversationId,
    setStreamingMessages,
    setLegacyMessages,
    setStreamingInput,
    setLegacyInput,
    setConversationScope,
    setPendingBasecampDraft,
    setPendingBasecampCreateDraft,
    streamingConversationId,
    hostedLinkedConversationId,
    lastLoadedScopePathsRef,
    stopPolling
  ])

  const handleShowHistory = useCallback(() => {
    openSidebarMode('chats')
  }, [])

  const handleSelectConversation = useCallback(async (id: string): Promise<{ ok: boolean; status?: number }> => {
    setShowHistory(false)

    // FIX 2: Block conversation switch while an unanswered AskQuestion is live (incident 2026-07-22).
    // Check streaming messages for any unanswered AskQuestion widgets.
    let hasUnansweredAsk = false
    setStreamingMessages((prev) => {
      hasUnansweredAsk = prev.some((m) => m.askQuestion && !m.askQuestion.answered)
      return prev // No-op update - just reading state
    })
    
    if (hasUnansweredAsk) {
      const { toast } = await import('sonner')
      toast.error('Please answer the question first', {
        description: 'You must respond to the current question before switching conversations.',
      })
      return { ok: false, status: 400 }
    }

    // Cross-surface, remount-surviving guard: some other path (this hook in a different/prior
    // mount, or the sessionStorage restore hook) already confirmed this id is gone. Skip the
    // doomed refetch entirely instead of adding another 404 to the pile.
    if (isKnownGoneConversationId(id)) {
      return { ok: false, status: 404 }
    }

    // Detach any in-flight stream before hydrating another conversation. Otherwise SSE
    // setStreamingMessages keep mutating the pane and "merge" foreign turns into the loaded chat
    // (incident 2026-07-22 askQuestion / chat-merge).
    // Skip when this pane is already streaming the conversation we are hydrating (new-chat
    // bind after first save) - otherwise restore/load aborts the send (pilot 2026-08-19).
    const optimisticHeld = Boolean(optimisticUserTurnRef?.current)
    const alreadyStreamingThis =
      (Boolean(streamInFlightRef?.current || abortControllerRef?.current) || optimisticHeld) &&
      (!streamingConversationId || streamingConversationId === id)
    if (!alreadyStreamingThis) {
      detachActiveStream()
    }
    // Draft is pane-local React state (not stored on ai_conversations). Clear immediately so a
    // prior chat's Basecamp Notes Draft cannot linger under a different conversation while load
    // is in flight. Session-snapshot restore (surface moves) re-applies the correct draft after.
    // Same-conversation remount (HMR / hosted load) must not wipe a live create card.
    if (!alreadyStreamingThis) {
      setPendingBasecampDraft?.(null)
      setPendingBasecampCreateDraft?.(null)
    }

    // Claim this load. If a newer handleSelectConversation/handleNewChat call happens before
    // this fetch resolves, `isStale()` will be true when we come back and we must not touch
    // state — otherwise this slower, now-irrelevant response overwrites the newer conversation's
    // messages (title already reflects the new selection; body would silently revert to stale data).
    const loadSeq = ++conversationLoadSeqRef.current
    const isStale = () => conversationLoadSeqRef.current !== loadSeq

    // Load conversation messages from backend — determine type first before setting state
    try {
      const result = await getConversation(id, true);
      if (isStale()) return { ok: true }
      const conversation = result.conversation;

      const scopePaths = conversation.workspace_scope
      const cached =
        peekThreadWorkspaceMetaByConversationId(id) ??
        peekThreadWorkspaceMeta(hostedThreadId)
      const hydrated = hydrateConversationDraftScope({
        scopePaths: Array.isArray(scopePaths) ? scopePaths : null,
        taggedOrganizationIds: cached?.taggedOrganizationIds,
        workspaces: workspacesRef?.current ?? [],
      })
      if (hydrated?.length) {
        setConversationScope?.(hydrated)
        if (userExplicitlySetScopeRef) userExplicitlySetScopeRef.current = true
        if (lastLoadedScopePathsRef) lastLoadedScopePathsRef.current = null
      } else {
        if (lastLoadedScopePathsRef) {
          lastLoadedScopePathsRef.current =
            scopePaths && Array.isArray(scopePaths) && scopePaths.length > 0
              ? scopePaths
              : null
        }
        const lookupId =
          (hostedThreadId && !hostedThreadId.startsWith('new-') ? hostedThreadId : null) ??
          cached?.threadId
        if (lookupId) {
          void getThread(lookupId)
            .then((thread) => {
              const meta = rememberThreadWorkspaceMeta(thread)
              if (isStale()) return
              const ids = hydrateConversationDraftScope({
                scopePaths: Array.isArray(scopePaths) ? scopePaths : null,
                taggedOrganizationIds: meta.taggedOrganizationIds,
                workspaces: workspacesRef?.current ?? [],
              })
              setConversationScope?.(ids)
              if (ids && userExplicitlySetScopeRef) {
                userExplicitlySetScopeRef.current = true
              }
            })
            .catch(() => {
              /* chip stays sidebar-fallback until tags load */
            })
        }
      }

      const isIngestion = conversation.metadata?.type === 'basecamp-ingestion' ||
                         conversation.metadata?.type === 'ingestion';

      if (isIngestion && isIngestionThreadComplete(conversation)) {
        await flipIngestionConversationToStreaming({
          stopPolling,
          setCurrentConversationId,
          setStreamingConversationId,
          setStreamingMessages,
          setLegacyMessages,
          setThinkingModeActive,
          conversationId: id,
          conversation,
        });
      } else if (isIngestion) {
        setCurrentConversationId(id)
        setStreamingConversationId(null)

        const chatMessages: Message[] = (conversation.messages || []).map(msg => ({
          role: msg.role as "user" | "assistant",
          content: msg.content,
          references: msg.references as DocumentReference[] | undefined,
          messageId: msg.id,
        }));
        setLegacyMessages(chatMessages);

        if (conversation.status === 'active') {
          startPolling(id, true);
        }
      } else {
        // Streaming / modern conversation — use chat-stream path, NOT refinement endpoint
        setCurrentConversationId(null)
        setStreamingConversationId(id)

        const chatMessages = mapApiMessagesToChatMessages(conversation.messages || [])
        if (alreadyStreamingThis) {
          setStreamingMessages((prev) => {
            if (chatMessages.length === 0) return prev
            return prependMissingUserTurns(prev, chatMessages)
          })
        } else {
          setStreamingMessages(chatMessages)
        }
        if (!alreadyStreamingThis) {
          setPendingBasecampCreateDraft?.(
            restoreBasecampCreateDraft(id, chatMessages) ||
              restoreBasecampCreateDraft(hostedLinkedConversationId, chatMessages),
          )
        }
        if (setCompletedFiles) {
          setCompletedFiles(
            completedFilesFromMessages(chatMessages).map((f) => ({
              filePath: f.filePath,
              documentId: f.documentId,
              title: f.title,
              changeStatus: f.changeStatus,
              status: 'modified' as const,
              linesAdded: f.linesAdded ?? 0,
              linesRemoved: f.linesRemoved ?? 0,
            })),
          )
        }
        const streamMeta = (conversation.metadata || {}) as Record<string, unknown>
        if (
          shouldKickoffSeededUpdateWorkSpec({
            source: streamMeta.source,
            messages: chatMessages,
          })
        ) {
          enqueueUpdateWorkSpecKickoff({
            targetConversationId: id,
            documentId:
              typeof streamMeta.document_id === 'string' ? streamMeta.document_id : null,
            skillId:
              typeof streamMeta.skill_id === 'string' ? streamMeta.skill_id : 'basecamp-todo',
          })
        }
      }
      return { ok: true }
    } catch (error) {
      // Superseded loads / StrictMode remounts can abort in-flight fetches; not a real failure.
      if (error instanceof Error && error.name === 'AbortError') {
        return { ok: false }
      }
      console.error('[ChatPane] Failed to load conversation:', error);
      // Surface HTTP status (set by getConversation, see conversations.ts) so callers
      // that auto-retry (e.g. the hosted-thread load effect) can stop retrying a
      // permanently-gone (404/410) conversation instead of looping forever.
      const status = (error as { status?: number } | undefined)?.status
      if (isPermanentlyGoneStatus(status)) {
        // Shared across every surface/mount (see conversation-load-guard.ts) — stops the
        // sessionStorage restore-on-mount path (chat-pane.custom-hooks.ts) and any other
        // hosted surface from re-attempting this same id too.
        markConversationIdGone(id)
        // This id may be sitting in the shared sessionStorage key (written by
        // useConversationIdPersistence from *any* surface's streamingConversationId). Clear it
        // now so an unrelated surface's mount-restore hook doesn't pick up this now-dead id.
        try {
          if (sessionStorage.getItem('flownotes_current_conversation_id') === id) {
            sessionStorage.removeItem('flownotes_current_conversation_id')
          }
        } catch {
          /* ignore */
        }
      }
      return { ok: false, status }
    }
  }, [
    detachActiveStream,
    setPendingBasecampDraft,
    setPendingBasecampCreateDraft,
    setCurrentConversationId,
    setStreamingConversationId,
    setStreamingMessages,
    setShowHistory,
    setLegacyMessages,
    startPolling,
    stopPolling,
    setConversationScope,
    lastLoadedScopePathsRef,
    setThinkingModeActive,
    setCompletedFiles,
    streamingConversationId,
    hostedLinkedConversationId,
    abortControllerRef,
    streamInFlightRef,
    hostedThreadId,
    userExplicitlySetScopeRef,
    workspacesRef,
    optimisticUserTurnRef,
  ])

  const handleCloseConversation = useCallback(async (id: string) => {
    console.log("Close conversation:", id)

    try {
      // Archive the conversation instead of just clearing
      await archiveConversation(id)

      // Remove from active conversations immediately
      setActiveConversations((prev) => prev.filter((c) => c.id !== id))

      // Clear current conversation if it's the one being closed
      if (currentConversationId === id) {
        setCurrentConversationId(null)
        setLegacyMessages([])
        setStreamingMessages([])
        setShowHistory(false)

        // Stop polling if active
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        setIsPolling(false)
      }

      // Refresh history panel
      setHistoryRefreshKey(prev => prev + 1)

      toast.success('Conversation archived')
    } catch (error) {
      console.error('[ChatPane] Failed to archive conversation:', error)
      toast.error('Failed to archive conversation', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }, [
    currentConversationId,
    setCurrentConversationId,
    setLegacyMessages,
    setStreamingMessages,
    setShowHistory,
    setActiveConversations,
    setHistoryRefreshKey,
    pollingIntervalRef,
    setIsPolling
  ])

  const handleArchiveConversation = useCallback(async (id: string) => {
    // Prevent multiple simultaneous archives of same conversation
    if (deletingConversationsRef.current.has(id)) {
      console.log('[ChatPane] BLOCKED: Already archiving conversation:', id)
      return
    }

    console.log("[ChatPane] Archive conversation START:", id)

    // Mark as archiving (ref updates immediately, no async)
    deletingConversationsRef.current.add(id)

    try {
      await archiveConversation(id)

      console.log('[ChatPane] ✅ Conversation archived successfully:', id)

      // Remove from active conversations
      setActiveConversations((prev) => prev.filter((c) => c.id !== id))

      // Close conversation if it's the active one
      if (currentConversationId === id) {
        setCurrentConversationId(null)
        setLegacyMessages([])
        setStreamingMessages([])
        setShowHistory(false)

        // Stop polling if active
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        setIsPolling(false)
      }

      // Trigger history panel refresh (without remounting - just reloads data)
      setHistoryRefreshKey(prev => prev + 1)

      toast.success('Conversation archived')
    } catch (error) {
      console.error('[ChatPane] ❌ Archive conversation error:', error)
      toast.error('Failed to archive conversation', {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
    } finally {
      // Remove from archiving set after delay to prevent rapid re-clicks
      setTimeout(() => {
        deletingConversationsRef.current.delete(id)
      }, 1000)
    }
  }, [
    currentConversationId,
    setCurrentConversationId,
    setLegacyMessages,
    setStreamingMessages,
    setShowHistory,
    setActiveConversations,
    setHistoryRefreshKey,
    pollingIntervalRef,
    setIsPolling,
    deletingConversationsRef
  ])

  const handleDeleteConversation = useCallback(async (id: string) => {
    // Keep for backward compatibility, but prefer archive
    await handleArchiveConversation(id)
  }, [handleArchiveConversation])

  const resolveExportConversationId = useCallback(() => {
    // Prefer the conversation bound to the hosted inbox/agent thread. Stale
    // currentConversationId can linger on an "Ingesting: ..." chat while the
    // panel title already shows a different thread (2026-07-21 pilot report).
    return (
      hostedLinkedConversationId ??
      streamingConversationId ??
      currentConversationId ??
      null
    )
  }, [hostedLinkedConversationId, streamingConversationId, currentConversationId])

  const handleExport = useCallback(async () => {
    const id = resolveExportConversationId()
    if (!id) {
      toast.error('No conversation to export')
      return
    }
    try {
      const blob = await exportConversation(id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `conversation-${id.slice(0, 8)}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Conversation exported')
    } catch {
      toast.error('Failed to export conversation')
    }
  }, [resolveExportConversationId])

  const handleCopyTranscript = useCallback(async () => {
    const id = resolveExportConversationId()
    if (!id) {
      toast.error('No conversation to copy')
      return
    }
    try {
      const blob = await exportConversation(id)
      const text = await blob.text()
      await navigator.clipboard.writeText(text)
      toast.success('Transcript copied to clipboard')
    } catch {
      toast.error('Failed to copy transcript')
    }
  }, [resolveExportConversationId])

  const handleCopyConversationId = useCallback(() => {
    const id = resolveExportConversationId()
    if (!id) {
      toast.error('No active conversation')
      return
    }
    navigator.clipboard.writeText(id).then(
      () => toast.success('Conversation ID copied'),
      () => toast.error('Failed to copy ID'),
    )
  }, [resolveExportConversationId])

  const handleRenameConversation = useCallback(async (id: string, newTitle: string) => {
    // Update active conversations state
    setActiveConversations((prev) =>
      prev.map((conv) =>
        conv.id === id
          ? { ...conv, title: newTitle }
          : conv
      )
    );

    // Refresh history panel
    setHistoryRefreshKey(prev => prev + 1);
  }, [setActiveConversations, setHistoryRefreshKey]);

  const handleRename = useCallback(() => {
    console.log("Rename conversation:", currentConversationId)
    // In real implementation, show rename dialog
  }, [currentConversationId])

  const handleDelete = useCallback(() => {
    console.log("Delete current conversation:", currentConversationId)
    // In real implementation, confirm and delete
  }, [currentConversationId])

  const handleClearHistory = useCallback(() => {
    console.log("Clear all history")
    // In real implementation, confirm and clear
  }, [])

  const handleSettings = useCallback(() => {
    openSettings({ tab: 'chat-skills' })
  }, [])

  return {
    handleNewChat,
    handleShowHistory,
    handleSelectConversation,
    handleCloseConversation,
    handleArchiveConversation,
    handleDeleteConversation,
    handleExport,
    handleCopyTranscript,
    handleCopyConversationId,
    handleRenameConversation,
    handleRename,
    handleDelete,
    handleClearHistory,
    handleSettings,
  }
}

